import {type Account} from '../../components/Search/SearchNav/types';
import {envIsIntegration, envIsServerDeployment} from '../../config';
import {
  type BillingPeriodSubscriptionsQuery,
  type InferenceCostUsageAggregation,
  type TrainingCostUsageAggregation,
  type UsageAggregation,
  type UsageAggregationInterface,
  UsageType,
  useUsageTabInfoQuery,
  useUsageTotalInfoQuery,
} from '../../generated/graphql';
import {type BillingPeriod, type LimitDateInterval} from '../accounts/pricing';
import {
  addDaysUTC,
  addMonthsUTC,
  differenceInCalendarDaysUTC,
  isAfter,
  isBefore,
  max,
  startOfDayUTC,
  startOfMonthUTC,
  subDaysUTC,
} from '../date';
import {removeNullUndefinedOrFalse} from '../utility';

export const EPOCH_START = new Date(0);
// Define TODAY locally to avoid circular import with MeterSection.tsx
// (MeterSection -> PlanCardComponents -> HistoricUsage -> HistoricUsageChartContextProvider -> utils -> MeterSection)
export const TODAY = new Date();
TODAY.setUTCHours(0, 0, 0, 0);
const TOMORROW = addDaysUTC(TODAY, 1);
const DEFAULT_NUMBER_OF_DAYS = 30;
const DEFAULT_DAYS_AGO = subDaysUTC(TOMORROW, DEFAULT_NUMBER_OF_DAYS);
const DEFAULT_INTERVAL_MARKERS = [DEFAULT_DAYS_AGO, TODAY];

// NOTE - months are 0-indexed, so this is currently July 11, 2024!
// This is the first day that we started collecting historic data for each UsageType
const EARLIEST_SAAS_STORAGE_DATA_DATE = new Date(2024, 6, 11);
EARLIEST_SAAS_STORAGE_DATA_DATE.setUTCHours(0, 0, 0, 0);

// Mid-month is hard to verify so we'll make Jan 1st 2025 first day for onprem storage data
const EARLIEST_ONPREM_STORAGE_DATA_DATE = new Date(2025, 0, 1);
EARLIEST_ONPREM_STORAGE_DATA_DATE.setUTCHours(0, 0, 0, 0);

export enum RolloutGroup {
  Onprem = 'onprem',
  Saas = 'saas',
}

export const EARLIEST_DATA_DATE: Partial<
  Record<RolloutGroup, Partial<Record<UsageType, Date>>>
> = {
  [RolloutGroup.Saas]: {
    [UsageType.Storage]: EARLIEST_SAAS_STORAGE_DATA_DATE,
  },
  [RolloutGroup.Onprem]: {
    [UsageType.Storage]: EARLIEST_ONPREM_STORAGE_DATA_DATE,
  },
};

export function getEarliestUsageRolloutDate(
  usageType: UsageType
): Date | undefined {
  if (envIsIntegration) {
    return; // no cutoff date for integration tests
  } else if (envIsServerDeployment) {
    return EARLIEST_DATA_DATE[RolloutGroup.Onprem]?.[usageType];
  } else {
    return EARLIEST_DATA_DATE[RolloutGroup.Saas]?.[usageType];
  }
}

// Top level variable since we don't want different aggregation calculations
// to use different dates around midnight
const currentDate = new Date();
currentDate.setUTCHours(0, 0, 0, 0);

export function valueAggregator(
  data: Pick<UsageAggregationInterface, 'start' | 'end' | 'value'>,
  usageType: UsageType,
  currentAggregatedValue: number,
  aggregationCutoffDate: Date | undefined
): number {
  switch (usageType) {
    case UsageType.Storage: {
      // The backend sends the summed total storage over all the days in the interval, so to get the average
      // calendar for the calendar month for the interval, divide each value by the number of days in the inclusive interval.
      let intervalStart = new Date(data.start);
      let intervalEnd = new Date(data.end);
      const usageTypeEarliestDataDate = getEarliestUsageRolloutDate(usageType);
      const earliestDataDate = max(
        removeNullUndefinedOrFalse([
          aggregationCutoffDate,
          usageTypeEarliestDataDate,
        ])
      ); // Choose whichever date is later to determine the earliest time we could have data

      /**
       * If the earliest data date falls within the interval, then we need to cut the interval short by moving the intervalStart up to the earliest data date for the org
       * If it's outside the interval, we do nothing:
       *     - If it's before the interval, we can divide by the whole interval length, so do nothing.
       *     - If it's after the interval, the value should be 0 anyway, so it doesn't matter what length we divide by, so do nothing.
       */
      if (
        earliestDataDate != null &&
        isBefore(intervalStart, earliestDataDate) &&
        isAfter(intervalEnd, earliestDataDate)
      ) {
        intervalStart = earliestDataDate;
      }

      // If it's the current month's interval, the interval's end needs to be moved back to the current date.
      if (
        isBefore(intervalStart, currentDate) &&
        isAfter(intervalEnd, currentDate)
      ) {
        intervalEnd = currentDate;
      }

      // Floor it up to 0 because if end is before start, that means it's before we have data, so we shouldn't divide by a negative number here (mostly applicable with mock data)
      const numDaysInInterval = Math.max(
        0,
        differenceInCalendarDaysUTC(intervalEnd, intervalStart)
      );

      return (
        currentAggregatedValue + data.value / (numDaysInInterval + 1) // add 1 since it's an inclusive interval
      );
    }
    default:
      return currentAggregatedValue + data.value;
  }
}

export const CURRENT_DAY_UTC_INTERVAL_MARKERS = [
  startOfDayUTC(TODAY),
  startOfDayUTC(TOMORROW),
];

const START_OF_CURRENT_CALENDAR_MONTH = startOfMonthUTC(TODAY);
const START_OF_NEXT_CALENDAR_MONTH = startOfMonthUTC(addMonthsUTC(TODAY, 1));
const CURRENT_CALENDAR_MONTH_INTERVAL_MARKERS = [
  START_OF_CURRENT_CALENDAR_MONTH,
  START_OF_NEXT_CALENDAR_MONTH,
];

type Subscription = {
  billingPeriodStart?: string | null;
  billingPeriodEnd?: string | null;
};

/**
 * Gets interval markers for a subscription and usage type.
 *
 * Args:
 *     subscription: The subscription with billing period dates.
 *     usageType: The type of usage to get interval markers for.
 *
 * Returns:
 *     Date[]: Array of [start, end] dates for the billing period.
 */
export function getIntervalMarkers(
  subscription: Subscription | null | undefined,
  usageType: UsageType
): Date[] {
  if (
    subscription?.billingPeriodStart == null ||
    subscription?.billingPeriodEnd == null
  ) {
    if (
      usageType === UsageType.Weave ||
      usageType === UsageType.InferenceCost
    ) {
      return CURRENT_CALENDAR_MONTH_INTERVAL_MARKERS;
    }
    return DEFAULT_INTERVAL_MARKERS;
  }
  const billingPeriodStart = new Date(subscription?.billingPeriodStart);
  const billingPeriodEnd = new Date(subscription?.billingPeriodEnd);
  if (usageType === UsageType.Storage) {
    // Since storage is an average value, we will add in the current value separately in case it hasn't been aggregated yet
    return [billingPeriodStart, TODAY];
  }
  return [billingPeriodStart, billingPeriodEnd];
}

/**
 * @deprecated use `useGetUsageThisBillingPeriod` instead.
 *
 * Legacy hook to get usage for a billing period. This uses the old UsageTabInfoQuery
 * and valueAggregator pattern. Prefer useGetUsageThisBillingPeriod for new code.
 */
export function useLegacyGetUsageTypeUsageThisBillingPeriod(
  orgName: string,
  orgCreatedAt: Date | undefined,
  usageType: UsageType,
  subscription: Subscription | null | undefined,
  currentValue: number, // we need to add today's real-time value to the total since it hasn't been aggregated yet
  skip: boolean
) {
  const intervalMarkers = getIntervalMarkers(subscription, usageType);
  const {data, loading, error} = useUsageTabInfoQuery({
    variables: {
      orgName,
      usageType,
      intervalMarkers: intervalMarkers.map(marker => marker.toISOString()),
    },
    skip: skip || isAfter(intervalMarkers[0], intervalMarkers[1]), // skip if the interval markers are out of order (i.e. the subscription started today)
  });
  const [intervalStart, intervalEnd] = getIntervalMarkers(
    subscription,
    usageType
  );
  intervalStart.setUTCHours(0, 0, 0, 0);
  const initialUsageData = {
    start: intervalStart.toISOString(),
    end: TODAY.toISOString(),
    value: currentValue,
  };
  const usageData = [
    initialUsageData,
    ...(data?.organization?.usage?.map(dataRow => ({
      ...dataRow,
      end: TODAY.toISOString(),
    })) ?? []),
  ]; // valueAggregator uses the `start` and `end` to calculate what to divide by for the interval - but since we added the current day's data, we should update it so the denominator's right
  const usage = usageData.reduce(
    (total, {value, start, end}) =>
      valueAggregator(
        {
          value,
          start,
          end,
        },
        usageType,
        total,
        orgCreatedAt
      ),
    0
  );
  return {
    usage,
    billingPeriodEnd: intervalEnd,
    billingPeriodStart: intervalStart,
    loading,
    error,
  };
}

export type WithRawUsageTotal = {usageTotal?: any[]} | null | undefined;
export type UsageTotal = number[] | undefined;
export function getNormalizedUsageTotal(
  withRawUsageTotal: WithRawUsageTotal,
  usageType: UsageType
): UsageTotal {
  const rawUsageTotal = withRawUsageTotal?.usageTotal;
  if (rawUsageTotal == null) {
    return undefined;
  }
  // If the usage total is an array, we assume it's a single value in the array
  return rawUsageTotal.map(rawValue =>
    normalizeUsageTotalBigInt(BigInt(rawValue ?? 0), usageType)
  );
}

export function normalizeUsageTotalBigInt(
  rawValue: bigint | number | null,
  usageType: UsageType
): number {
  rawValue = BigInt(rawValue ?? 0); // Ensure rawValue is a BigInt
  switch (usageType) {
    case UsageType.InferenceCost:
    case UsageType.TrainingCost:
    case UsageType.WbAgentCost:
    case UsageType.SandboxesCost:
    case UsageType.ModelsPointsCost:
      // Convert from billionth of cents to cents
      return Number(rawValue / BigInt(1_000_000_000));
    case UsageType.Storage:
    case UsageType.Weave:
    case UsageType.TrackedHours:
    case UsageType.ModelsPoints:
    default:
      return Number(rawValue);
  }
}

export type UsageAggregationTypes =
  | UsageAggregation
  | InferenceCostUsageAggregation
  | TrainingCostUsageAggregation;

export function useGetUsageThisBillingPeriod(
  account: Account | null,
  usageType: UsageType,
  intervalMarkers: Date[],
  skip: boolean
) {
  const {data, loading, error, refetch} = useUsageTotalInfoQuery({
    variables: {
      orgName: account?.name ?? '',
      usageType,
      intervalMarkers: intervalMarkers.map(marker => marker.toISOString()),
    },
    skip:
      skip ||
      account?.name == null ||
      !areValidIntervalMarkers(intervalMarkers), // skip if the interval markers are out of order (i.e. the subscription started today)
  });
  const usage =
    getNormalizedUsageTotal(data?.organization, usageType)?.reduce(
      (acc, val) => acc + val,
      0
    ) ?? 0;

  return {
    usage,
    loading,
    error,
    refetch,
  };
}

export function areValidIntervalMarkers(dates: Date[]): boolean {
  if (dates.length < 2) {
    return false; // Need at least two dates to form a valid interval
  }
  for (let i = 1; i < dates.length; i++) {
    if (dates[i - 1].getTime() > dates[i].getTime()) {
      return false;
    }
  }
  return true;
}

type BillingPeriodOrganization = NonNullable<
  BillingPeriodSubscriptionsQuery['organization']
>;
type BillingPeriodSubscription = Pick<
  BillingPeriodOrganization['subscriptions'][number],
  'billingPeriodStart' | 'billingPeriodEnd'
>;

export function getIntervalMarkersFromBillingPeriod(
  billingPeriod: BillingPeriod | null | undefined
): Date[] | null {
  return getIntervalMarkersFromSubscription(billingPeriod);
}

export function getIntervalMarkersFromSubscription(
  subscription: BillingPeriodSubscription | null | undefined
): Date[] | null {
  if (
    subscription?.billingPeriodEnd == null ||
    subscription?.billingPeriodStart == null
  ) {
    return null;
  }
  const billingPeriodStart = startOfDayUTC(
    new Date(subscription.billingPeriodStart)
  );
  const billingPeriodEnd = startOfDayUTC(
    new Date(subscription.billingPeriodEnd)
  );
  return [billingPeriodStart, billingPeriodEnd];
}

export function getIntervalMarkersFromCurrentCalendarMonth(): Date[] {
  const today = startOfDayUTC(new Date());
  return [startOfMonthUTC(today), addMonthsUTC(startOfMonthUTC(today), 1)];
}

export function getIntervalMarkersFromLimitDateInterval(
  limitDateInterval: LimitDateInterval
): Date[] {
  const billingPeriodStart = startOfDayUTC(new Date(limitDateInterval.start));
  const billingPeriodEnd = startOfDayUTC(new Date(limitDateInterval.end));
  return [billingPeriodStart, billingPeriodEnd];
}

/**
 * Formats overage charge text for display.
 *
 * Args:
 *     cost (number): The cost in dollars.
 *
 * Returns:
 *     string: Formatted overage charge text.
 *
 * Examples:
 *     formatOverageChargeText(10.5) // "Overage charge: $10.50"
 */
export function formatOverageChargeText(cost: number): string {
  return `Overage charge: ${new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cost)}`;
}

export const ZERO_COST_TEXT = formatOverageChargeText(0);
